import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Send, Gamepad2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface IdeaForm {
  title: string;
  description: string;
  category: string;
  honeypot: string;
}

export default function BetaIdeas() {
  const { user } = useAuth();
  const [form, setForm] = useState<IdeaForm>({
    title: "",
    description: "",
    category: "",
    honeypot: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof IdeaForm, string>>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof IdeaForm, string>> = {};

    // Title validation
    if (!form.title.trim()) {
      newErrors.title = "Title is required";
    } else if (form.title.trim().length < 3) {
      newErrors.title = "Title must be at least 3 characters";
    } else if (form.title.trim().length > 200) {
      newErrors.title = "Title must be less than 200 characters";
    }

    // Description validation
    if (!form.description.trim()) {
      newErrors.description = "Description is required";
    } else if (form.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (form.description.trim().length > 2000) {
      newErrors.description = "Description must be less than 2000 characters";
    }

    // Category is optional, but if provided, should be valid
    if (form.category && !["feature", "ui", "bug_fix", "performance", "other"].includes(form.category)) {
      newErrors.category = "Invalid category";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof IdeaForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot
    if (form.honeypot) {
      toast.error("Spam detected. Please try again.");
      return;
    }

    // Validate form
    if (!validateForm()) {
      toast.error("Please fix the errors in the form.");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to submit an idea.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("beta_ideas")
        .insert({
          user_id: user.id,
          title: form.title.trim(),
          description: form.description.trim(),
          category: form.category || null,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Your idea has been submitted successfully! Thank you for your feedback.");

      // Reset form
      setForm({
        title: "",
        description: "",
        category: "",
        honeypot: "",
      });
    } catch (error: any) {
      console.error("Error submitting idea:", error);
      toast.error(error.message || "Failed to submit idea. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Lightbulb className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Beta Ideas</h1>
          </div>
          <p className="text-muted-foreground">
            Share your ideas and feedback to help us improve IndieKeys during the beta period.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Submit an Idea</CardTitle>
              <CardDescription>
                Describe your idea or suggestion in detail. All ideas are welcome!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <Label htmlFor="title">
                    Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Brief title for your idea"
                    maxLength={200}
                    className={errors.title ? "border-destructive" : ""}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive mt-1">{errors.title}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {form.title.length}/200 characters
                  </p>
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Describe your idea in detail. What problem does it solve? How would it work?"
                    rows={6}
                    maxLength={2000}
                    className={errors.description ? "border-destructive" : ""}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive mt-1">{errors.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {form.description.length}/2000 characters
                  </p>
                </div>

                {/* Category */}
                <div>
                  <Label htmlFor="category">Category (Optional)</Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) => handleInputChange("category", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="ui">UI/UX</SelectItem>
                      <SelectItem value="bug_fix">Bug Fix</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="text-sm text-destructive mt-1">{errors.category}</p>
                  )}
                </div>

                {/* Honeypot */}
                <div style={{ display: "none" }}>
                  <Label htmlFor="honeypot">Leave this field empty</Label>
                  <Input
                    id="honeypot"
                    type="text"
                    value={form.honeypot}
                    onChange={(e) => handleInputChange("honeypot", e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Submit Button */}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Gamepad2 className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Idea
                    </>
                  )}
                </Button>

                <div className="mt-4 text-[0.7rem] text-muted-foreground leading-relaxed">
                  By submitting this idea, you agree that your data will be processed to handle your suggestion. In accordance with GDPR, you have the right to access, rectify, and delete your data. For more information, please see our <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

